import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import moment from 'moment';
import { useConfirm } from 'material-ui-confirm';

import {
  deleteCourseTutor,
  getCourseTutorsState,
  ICourseTutor,
  TCourseTutorType,
} from '../../../../../../store/V2/CourseTutorsSlice';
import {
  deleteCourseStudent,
  getCourseStudentsState,
  ICourseStudent,
} from '../../../../../../store/V2/CourseStudentsSlice';
import { IUser } from '../../../../../../store/UserSlice';
import {
  getCoursesState,
  TUpdateCourseAsAdmin,
} from '../../../../../../store/V2/CoursesSlice';
import { getAuthState } from '../../../../../../store/AuthSlice';
import { updateCourseAsAdminSavingSlice } from '../../../../../../store/V2/SavingSlice';
import { useAppSelector } from '../../../../../../hooks';
import { checkUserPermission } from '../../../../../../helper';
import BasicLoader from '../../../../../../components/Loaders/BasicLoader';
import AddCourseTutorModal from '../CourseOptionsModal/TutorsTab/modals/AddCourseTutorModal';
import AddCourseStudentModal from '../CourseOptionsModal/StudentsTab/modals/AddCourseStudentModal';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';

interface Step2Props {
  moveToNextStep: () => void;
  moveToPreviousStep: () => void;
}

type UserCardProps = {
  disabled?: boolean;
  itemId: string;
  type: 'tutor' | 'student';
  user: IUser;
  startDate?: string;
  endDate?: string;
  title?: string;
  description?: string;
  courseTutorType?: TCourseTutorType;
};

const userCardContainerStyles: SxProps = {
  backgroundColor: '#F8F9FD',
  borderRadius: '12px',
};

const Step2: React.FC<Step2Props> = ({
  moveToNextStep,
  moveToPreviousStep,
}) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const courseTutorsState = useAppSelector(getCourseTutorsState);
  const courseStudentsState = useAppSelector(getCourseStudentsState);
  const authState = useAppSelector(getAuthState);

  // State
  const [initialLoading, setInitialLoading] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isAddCourseTutorModalOpen, setAddCourseTutorModalOpen] = useState(
    false
  );

  const [isAddCourseStudentModalOpen, setAddCourseStudentModalOpen] = useState(
    false
  );

  const { adminCourseTutorsData } = courseTutorsState;
  const { adminCourseStudentsData } = courseStudentsState;

  let tutors: ICourseTutor[] = [];
  let students: ICourseStudent[] = [];

  const authUser = authState.user;
  const courseData = coursesState.adminCourse;
  const isOneToOne = Boolean(courseData && courseData.isOneToOne);

  if (
    adminCourseTutorsData &&
    adminCourseTutorsData.list &&
    adminCourseTutorsData.list.length
  ) {
    tutors = [...adminCourseTutorsData.list];
  }

  if (
    adminCourseStudentsData &&
    adminCourseStudentsData.list &&
    adminCourseStudentsData.list.length
  ) {
    students = [...adminCourseStudentsData.list];
  }

  const isNextDisabled =
    initialLoading ||
    courseTutorsState.status === 'loading' ||
    courseStudentsState.status === 'loading' ||
    !tutors.length ||
    (isOneToOne && !students.length);

  const toggleAddCourseTutorModal = () => {
    setAddCourseTutorModalOpen(!isAddCourseTutorModalOpen);
  };

  const toggleAddCourseStudentModal = () => {
    setAddCourseStudentModalOpen(!isAddCourseStudentModalOpen);
  };

  const handleDelete = (id: string, type: UserCardProps['type']) => {
    confirm({
      title: 'Are you sure?',
      description: `Do you want to delete?`,
    }).then(() => {
      if (courseData && type === 'tutor') {
        dispatch(
          deleteCourseTutor({
            id,
            course: courseData._id,
          })
        );
      } else if (courseData && type === 'student') {
        dispatch(deleteCourseStudent(id));
      }
    });
  };

  const handleSubmit = async () => {
    if (courseData) {
      setLoading(true);

      const apiData: TUpdateCourseAsAdmin = {
        _id: courseData._id,
        isStep2Completed: true,
      };

      await dispatch(updateCourseAsAdminSavingSlice(apiData));

      setLoading(false);

      moveToNextStep();
    }
  };

  const UserCard = ({
    disabled,
    type,
    itemId,
    user,
    startDate,
    endDate,
    title,
    description,
    courseTutorType,
  }: UserCardProps) => {
    return (
      <Card elevation={0} sx={userCardContainerStyles}>
        <CardContent style={{ padding: '16px' }}>
          {user ? (
            <Stack direction="row" alignItems="center" gap={2} mb="15px">
              <Avatar src={user.avatar} />
              <Stack flexGrow={1}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user.firstname}
                  {user.lastname ? ` ${user.lastname}` : ''}
                </Typography>
                <Typography variant="caption">{user.email}</Typography>
              </Stack>
              {courseTutorType ? (
                <Chip
                  size="small"
                  color="default"
                  label={courseTutorType.toLowerCase()}
                  sx={{ textTransform: 'capitalize' }}
                />
              ) : null}
            </Stack>
          ) : null}
          {startDate ? (
            <Stack mb="15px">
              <Typography variant="subtitle2" fontWeight={600}>
                Start Date
              </Typography>
              <Typography>
                {moment(startDate).format('ddd MMM DD YYYY')}
              </Typography>
            </Stack>
          ) : null}
          {endDate ? (
            <Stack mb="15px">
              <Typography variant="subtitle2" fontWeight={600}>
                End Date
              </Typography>
              <Typography>
                {moment(endDate).format('ddd MMM DD YYYY')}
              </Typography>
            </Stack>
          ) : null}
          {title ? (
            <Stack mb="15px">
              <Typography variant="subtitle2" fontWeight={600}>
                Title
              </Typography>
              <Typography>{title}</Typography>
            </Stack>
          ) : null}
          {description ? (
            <Stack mb="15px">
              <Typography variant="subtitle2" fontWeight={600}>
                Description
              </Typography>
              <div
                className="editor-container"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: description || '',
                }}
              />
            </Stack>
          ) : null}
          {checkUserPermission(authUser, {
            adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
          }) ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={2}
            >
              <ButtonBase
                disabled={disabled}
                onClick={() => handleDelete(itemId, type)}
                sx={{
                  backgroundColor: '#F1F5F9',
                  padding: '5px',
                  borderRadius: '5px',
                }}
              >
                <DeleteIcon color="error" />
              </ButtonBase>
            </Stack>
          ) : null}
        </CardContent>
      </Card>
    );
  };

  return (
    <div>
      {initialLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '350px',
          }}
        >
          <BasicLoader />
        </div>
      ) : (
        <div style={{ minHeight: '350px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">
                Tutor{!isOneToOne ? 's' : null}
              </Typography>
            </Grid>
            {tutors.length
              ? tutors.map((tutor) => {
                  return (
                    <Grid item xs={6} key={tutor._id}>
                      <UserCard
                        type="tutor"
                        itemId={tutor._id}
                        user={tutor.tutor}
                        startDate={tutor.startDate}
                        endDate={tutor.endDate}
                        title={tutor.title}
                        courseTutorType={tutor.type}
                        description={tutor.description}
                        disabled={
                          courseTutorsState.status === 'loading' ||
                          courseStudentsState.status === 'loading'
                        }
                      />
                    </Grid>
                  );
                })
              : null}
            <Grid item xs={12}>
              <Button
                variant="text"
                color="primary"
                startIcon={<AddIcon />}
                onClick={toggleAddCourseTutorModal}
              >
                Add Tutor
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6">
                Student{!isOneToOne ? 's' : null}
              </Typography>
            </Grid>
            {students.length
              ? students.map((student) => {
                  return (
                    <Grid item xs={6} key={student._id}>
                      <UserCard
                        type="student"
                        itemId={student._id}
                        user={student.student}
                        startDate={student.startDate}
                        endDate={student.endDate}
                        disabled={
                          courseTutorsState.status === 'loading' ||
                          courseStudentsState.status === 'loading'
                        }
                      />
                    </Grid>
                  );
                })
              : null}
            {!isOneToOne || (isOneToOne && !students.length) ? (
              <Grid item xs={12}>
                <Button
                  variant="text"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={toggleAddCourseStudentModal}
                >
                  Add Student
                </Button>
              </Grid>
            ) : null}
          </Grid>
        </div>
      )}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt="15px"
      >
        <Button
          disabled={
            initialLoading ||
            courseTutorsState.status === 'loading' ||
            courseStudentsState.status === 'loading'
          }
          variant="text"
          color="primary"
          onClick={moveToPreviousStep}
        >
          Back
        </Button>
        <LoadingButton
          variant="text"
          color="primary"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={isNextDisabled}
        >
          Next
        </LoadingButton>
      </Stack>
      {/* Modals */}
      {isAddCourseTutorModalOpen ? (
        <AddCourseTutorModal
          open={isAddCourseTutorModalOpen}
          onClose={toggleAddCourseTutorModal}
        />
      ) : null}
      {isAddCourseStudentModalOpen ? (
        <AddCourseStudentModal
          open={isAddCourseStudentModalOpen}
          onClose={toggleAddCourseStudentModal}
        />
      ) : null}
    </div>
  );
};

export default Step2;
